import { useRef, useState } from "react";
import {
  updateSaleDepositNote,
  updateSaleDepositReview,
} from "../../services";
import { SaleDepositResponse } from "../../types/sale-deposit.type";

/** Which ตรวจแล้ว checkbox was clicked. The server decides the side from the caller's roles;
 * this only says which field the row should reflect while the request is in flight. */
export type DepositReviewSide = "bo" | "ho";

/** The review fields a toggle can change — everything else on the row is left untouched. */
export type DepositReviewPatch = Partial<
  Pick<
    SaleDepositResponse,
    | "bo_reviewed"
    | "bo_reviewed_by_name"
    | "bo_reviewed_at"
    | "ho_reviewed"
    | "ho_reviewed_by_name"
    | "ho_reviewed_at"
  >
>;

const reviewFieldsOf = (row: SaleDepositResponse): DepositReviewPatch => ({
  bo_reviewed: row.bo_reviewed,
  bo_reviewed_by_name: row.bo_reviewed_by_name,
  bo_reviewed_at: row.bo_reviewed_at,
  ho_reviewed: row.ho_reviewed,
  ho_reviewed_by_name: row.ho_reviewed_by_name,
  ho_reviewed_at: row.ho_reviewed_at,
});

/** Only the flag moves optimistically: the reviewer's name is the server's to assign. */
const flagPatch = (side: DepositReviewSide, reviewed: boolean): DepositReviewPatch =>
  side === "bo" ? { bo_reviewed: reviewed } : { ho_reviewed: reviewed };

/**
 * Shared review/note mutations for the Sale Deposit records. Used by BOTH the HO
 * Sale Deposit Report and the BO Delivery Information table so both stay in sync
 * (same backend record). The consumer owns its row list and passes callbacks to
 * patch a single row by its deposit uuid.
 *
 * The review toggle is optimistic and guards against out-of-order responses: a
 * per-uuid sequence number is bumped on every request; only the response from
 * the latest request for that uuid is allowed to write state back (older
 * in-flight responses are ignored so rapid toggles never overwrite the newest
 * intent).
 */
export function useDepositActions(
  onReviewChange: (uuid: string, patch: DepositReviewPatch) => void,
  onNoteChange: (uuid: string, note: string | null) => void,
) {
  const [reviewLoading, setReviewLoading] = useState<Record<string, boolean>>(
    {},
  );
  const [noteSaving, setNoteSaving] = useState<boolean>(false);
  const seqRef = useRef<Record<string, number>>({});

  /**
   * Resolves true only when THIS call's write was accepted and is still the row's latest, so
   * a caller can reload what is counted from the ticks. A refusal answers false, and so does
   * a response a newer toggle has already superseded — that newer one reports for itself.
   */
  const toggleReview = async (
    uuid: string,
    side: DepositReviewSide,
    next: boolean,
  ): Promise<boolean> => {
    const mySeq = (seqRef.current[uuid] ?? 0) + 1;
    seqRef.current[uuid] = mySeq;

    // Optimistic: reflect the intended value immediately.
    onReviewChange(uuid, flagPatch(side, next));
    setReviewLoading((prev) => ({ ...prev, [uuid]: true }));

    try {
      const {
        data: { data },
      } = await updateSaleDepositReview(uuid, next);
      // Ignore stale responses (a newer toggle superseded this one).
      if (seqRef.current[uuid] !== mySeq) return false;
      const row = data as SaleDepositResponse | null;
      // The response carries who signed, which the optimistic patch could not know.
      onReviewChange(uuid, row ? reviewFieldsOf(row) : flagPatch(side, next));
      return true;
    } catch {
      // Revert the optimistic change only if this is still the latest request.
      if (seqRef.current[uuid] === mySeq) {
        onReviewChange(uuid, flagPatch(side, !next));
      }
      return false;
    } finally {
      if (seqRef.current[uuid] === mySeq) {
        setReviewLoading((prev) => ({ ...prev, [uuid]: false }));
      }
    }
  };

  const saveNote = async (
    uuid: string,
    note: string | null,
  ): Promise<boolean> => {
    setNoteSaving(true);
    try {
      const {
        data: { data },
      } = await updateSaleDepositNote(uuid, note);
      const saved = (data as SaleDepositResponse | null)?.note ?? note;
      onNoteChange(uuid, saved);
      return true;
    } catch {
      return false;
    } finally {
      setNoteSaving(false);
    }
  };

  return { reviewLoading, toggleReview, noteSaving, saveNote };
}
